import * as React from 'react'

import { MobXProviderContext } from 'mobx-react'

import { PropertyType } from '~/client/graph'
import { IResourceProperty } from '~/client/src/shared/components/ResourceCardList/types'
import {
  getResourceQuickFilterDefinitions,
  IResourceQuickFilterDefinition,
} from '~/client/src/shared/hooks/useResourceQuickFilters'
import { useResourceRuntimeContext } from '~/client/src/shared/hooks/useResourceRuntimeContext'
import { getResourceSortingOptions } from '~/client/src/shared/resources/builders/getResourceFieldOptions'
import { buildResourceFieldCatalogFromProperties } from '~/client/src/shared/resources/catalog/buildResourceFieldCatalog'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'
import { createMixedResourceFieldProjector } from '~/client/src/shared/resources/projection/createMixedResourceFieldProjector'
import { createResourceFieldProjector } from '~/client/src/shared/resources/projection/createResourceFieldProjector'
import { IResourceFieldProjector } from '~/client/src/shared/resources/projection/types'
import { getProjectorStores } from '~/client/src/shared/resources/projection/useResourceFieldProjector'
import { IHierarchyAccessors } from '~/client/src/shared/utils/hierarchyAccessors'

/** One resource type in a picker. Ids must be unique across sources. */
export interface IResourcePickerTypeSource {
  resourceTypeId: string
  label?: string
  icon?: React.ReactNode
  records: IResourceRecord[]
  properties?: IResourceProperty[]
  resourceSchema?: ResourceSchema
  /** This type's row, where the list is given none of its own. */
  renderItem?: (record: IResourceRecord) => React.ReactNode
}

export interface IResourceTypeSources {
  isMixed: boolean
  records: IResourceRecord[]
  getRecordTypeId: (record: IResourceRecord) => string
  fieldProjector: IResourceFieldProjector
  searchableKeys: string[]
  quickFilterDefinitions: IResourceQuickFilterDefinition[]
  /** Keys every type can sort by; null for a single type. */
  sortKeys: ReadonlySet<string> | null
  getFieldHierarchy: (
    resourceTypeId: string,
    key: string,
  ) => IHierarchyAccessors | undefined
  getPropertyConfig: (
    resourceTypeId: string,
    key: string,
  ) => IResourceProperty['configuration'] | undefined
}

// The same array while every item is the same, so derivations keyed on one aspect of
// the sources (their properties, say) don't rebuild when another (records) changes.
function useStableList<T>(list: readonly T[]): readonly T[] {
  const ref = React.useRef(list)
  if (
    ref.current.length !== list.length ||
    ref.current.some((item, index) => item !== list[index])
  ) {
    ref.current = list
  }
  return ref.current
}

const propertyKeysOfType = (
  properties: IResourceProperty[] | undefined,
  propertyType: PropertyType,
) =>
  (properties || [])
    .filter(property => property.propertyType === propertyType)
    .map(property => property.name)

// Per-type catalogs and projectors, combined. A single source is the one-type case:
// its own records, projector and quick filters, unwrapped.
export function useResourceTypeSources(
  sources: readonly IResourcePickerTypeSource[],
  {
    currentUserId,
    currentUserCompanyId,
  }: { currentUserId?: string; currentUserCompanyId?: string },
): IResourceTypeSources {
  const stores = React.useContext(MobXProviderContext) as any
  const runtimeContext = useResourceRuntimeContext()

  const typeIds = useStableList(sources.map(source => source.resourceTypeId))
  const propertyLists = useStableList(sources.map(source => source.properties))
  const recordLists = useStableList(sources.map(source => source.records))
  const isMixed = typeIds.length > 1
  const primaryTypeId = typeIds[0]

  const catalogs = React.useMemo(
    () =>
      typeIds.map((resourceTypeId, index) =>
        buildResourceFieldCatalogFromProperties(
          resourceTypeId,
          propertyLists[index],
        ),
      ),
    [typeIds, propertyLists],
  )

  const projectors = React.useMemo(
    () =>
      typeIds.map((resourceTypeId, index) =>
        createResourceFieldProjector({
          catalog: catalogs[index],
          resourceTypeId,
          runtimeContext,
          ...getProjectorStores(stores),
        }),
      ),
    [typeIds, catalogs, runtimeContext, stores],
  )

  const records = React.useMemo(
    () => (isMixed ? recordLists.flat() : recordLists[0]),
    [isMixed, recordLists],
  )

  const typeIdById = React.useMemo(() => {
    if (!isMixed) return null
    const map = new Map<string, string>()
    recordLists.forEach((list, index) =>
      list.forEach(record => map.set(record.id, typeIds[index])),
    )
    return map
  }, [isMixed, recordLists, typeIds])

  // Through a ref, so a records update doesn't rebuild what reads the type.
  const typeIdByIdRef = React.useRef(typeIdById)
  typeIdByIdRef.current = typeIdById
  const getRecordTypeId = React.useCallback(
    (record: IResourceRecord) =>
      typeIdByIdRef.current?.get(record.id) ?? primaryTypeId,
    [primaryTypeId],
  )

  const fieldProjector = React.useMemo(
    () =>
      isMixed
        ? createMixedResourceFieldProjector({
            projectorByType: new Map(
              typeIds.map((resourceTypeId, index) => [
                resourceTypeId,
                projectors[index],
              ]),
            ),
            getRecordTypeId,
            primary: projectors[0],
          })
        : projectors[0],
    [isMixed, typeIds, projectors, getRecordTypeId],
  )

  const searchableKeys = React.useMemo(
    () =>
      Array.from(
        new Set(
          catalogs.flatMap(catalog =>
            catalog.searchableFields.map(field => field.key),
          ),
        ),
      ),
    [catalogs],
  )

  const quickFilterDefinitions = React.useMemo(() => {
    const perType = typeIds.map((resourceTypeId, index) =>
      getResourceQuickFilterDefinitions({
        resourceTypeId,
        userPropertyKeys: propertyKeysOfType(
          propertyLists[index],
          PropertyType.User,
        ),
        companyPropertyKeys: propertyKeysOfType(
          propertyLists[index],
          PropertyType.Company,
        ),
        currentUserId,
        currentUserCompanyId,
      }),
    )
    if (!isMixed) return perType[0]

    const byType = new Map(
      typeIds.map((resourceTypeId, index) => [
        resourceTypeId,
        new Map(perType[index].map(definition => [definition.key, definition])),
      ]),
    )
    return perType[0].map(definition => ({
      ...definition,
      isAvailable: Array.from(byType.values()).some(
        definitions => definitions.get(definition.key)?.isAvailable,
      ),
      matchesRecord: (record: IResourceRecord) =>
        !!byType
          .get(getRecordTypeId(record))
          ?.get(definition.key)
          ?.matchesRecord(record),
    }))
  }, [
    isMixed,
    typeIds,
    propertyLists,
    currentUserId,
    currentUserCompanyId,
    getRecordTypeId,
  ])

  const sortKeys = React.useMemo(() => {
    if (!isMixed) return null
    const keySets = typeIds.map(
      (resourceTypeId, index) =>
        new Set(
          getResourceSortingOptions(
            resourceTypeId,
            propertyLists[index],
            catalogs[index],
          ).map(option => option.key),
        ),
    )
    return new Set(
      Array.from(keySets[0]).filter(key => keySets.every(set => set.has(key))),
    )
  }, [isMixed, typeIds, propertyLists, catalogs])

  const getFieldHierarchy = React.useCallback(
    (resourceTypeId: string, key: string) =>
      catalogs[typeIds.indexOf(resourceTypeId)]?.getField(key)?.hierarchy,
    [catalogs, typeIds],
  )

  const getPropertyConfig = React.useCallback(
    (resourceTypeId: string, key: string) =>
      propertyLists[typeIds.indexOf(resourceTypeId)]?.find(
        property => property.name === key,
      )?.configuration,
    [propertyLists, typeIds],
  )

  return {
    isMixed,
    records,
    getRecordTypeId,
    fieldProjector,
    searchableKeys,
    quickFilterDefinitions,
    sortKeys,
    getFieldHierarchy,
    getPropertyConfig,
  }
}
